import { useMutation, useQuery } from "react-query";
import { mdlParameterDetailRequest } from "../../models/service-models/parameter/ParameterDetailRequest";
import { ParameterService } from "../../services/ParameterService";
import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, Input, Select, Checkbox, DatePicker, Typography, Button, Space, Spin, Skeleton } from "antd";
import { enmParameterType } from "../../models/enums/ParameterDataType";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { mdlParameterUpdateRequest } from "../../models/service-models/parameter/ParameterUpdateRequest";
import { mdlParameter } from "../../models/domain/Parameter";
import { useAppDispatch, useAppSelector } from "../../store/Hooks";
import { setUserHotels } from "../../store/SiteSlice";

const { Text } = Typography;

export type ParameterDetailProps = {
    parameterId?: string;
    hotelId?: string;
    hotelParameterId?: string;
    callback?: () => void;
};

export const ParameterDetail = memo(function ParameterDetail({
    parameterId,
    hotelId,
    hotelParameterId,
    callback,
}: ParameterDetailProps) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const userHotels = useAppSelector(x => x.site.userHotels);
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (!parameterId) {
            form.resetFields();
        }
    }, [parameterId, form]);

    const { data, isLoading, isError, isFetched } = useQuery(
        ["getParameterDetail", hotelParameterId],
        () => {
            if (parameterId) {
                const request: mdlParameterDetailRequest = { parameterId, hotelId, hotelParameterId };
                return ParameterService.Detail(request);
            }
        },
        {
            staleTime: 0,
            refetchOnWindowFocus: false,
            enabled: !!parameterId,
        }
    );

    // Veri geldikten sonra formu doldur
    useEffect(() => {
        if (data?.body?.data.hotelParameter) {
            var { value1, parameterDataType, defaultValue } = data.body.data.hotelParameter;
            if (value1 == null) value1 = defaultValue
            // Checkbox durumu için boolean değere çevirme
            form.setFieldsValue({
                value1: value1 === "TRUE" ? true : value1 === "FALSE" ? false : value1,
                parameterDataType
            });
        }
    }, [data, form]);

    const renderInputField = (type: number) => {
        switch (type) {
            case enmParameterType.TEXT:
                return (
                    <Form.Item name="value1" label={t("common.value")}>
                        <Input />
                    </Form.Item>
                );
            case enmParameterType.TEXTAREA:
                return (
                    <Form.Item name="value1" label={t("common.value")}>
                        <Input.TextArea />
                    </Form.Item>
                );
            case enmParameterType.CHECKBOX:
                return (
                    <Form.Item name="value1" valuePropName="checked" label={t("common.value")}>
                        <Checkbox />
                    </Form.Item>
                );
            case enmParameterType.SELECT:
                return (
                    <Form.Item name="value1" label={t("common.value")}>
                        <Select
                            loading={isLoading}
                        >
                            {data?.body?.data.hotelParameter?.items?.map((item: { name: string, value: string }) => {
                                return (
                                    <Select.Option key={item.value} value={item.value}>
                                        {item.name}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                );
            case enmParameterType.DATE:
                return (
                    <Form.Item name="value1" label={t("common.value")}>
                        <DatePicker />
                    </Form.Item>
                );
            case enmParameterType.DATE_RANGE:
                return (
                    <Form.Item name="value1" label={t("common.value")}>
                        <DatePicker.RangePicker />
                    </Form.Item>
                );
            default:
                return null;
        }
    };

    const { mutateAsync: saveParameter, isLoading: mutateLoading } = useMutation(
        (data: mdlParameterUpdateRequest) => ParameterService.Update(data),
        {
            onMutate(variables) {
                Object.assign(variables, {
                    hotelId,
                    parameterId, hotelParameterId
                });
            },
            onError: (error) => {
                console.error(error);
                toast.error(t("common.errorSaving"));
            },
            onSuccess: (data) => {
                if (data.header?.success) {
                    const updatedUserHotels = userHotels?.map((hotel) => {
                        if (hotel.id === hotelId) {
                            return {
                                ...hotel,
                                hotelWebParameters: hotel.hotelWebParameters?.map((param) =>
                                    param.parameterId === parameterId
                                        ? { ...param, value1: form.getFieldValue("value1") === true ? "TRUE" : form.getFieldValue("value1") === false ? "FALSE" : form.getFieldValue("value1") }
                                        : param
                                ),
                            };
                        }

                        return hotel;
                    });
                    if (updatedUserHotels != null)
                        dispatch(setUserHotels(updatedUserHotels));
                    toast.success(t("common.success"));
                    form.resetFields();
                    callback?.();
                } else {
                    toast.error(ObjectHelper.getErrorMessage(data.header?.messages));
                }
            },
        }
    );

    const handleSubmit = async (values: any) => {
        if (values.value1 === true || values.value1 === false) {
            values.value1 = values.value1 ? "TRUE" : "FALSE";
        }
        const requestData = { value: values.value1 };
        await saveParameter(requestData);
    };

    if (isLoading) return <>
        <Skeleton active />
        <Skeleton active />
    </>;
    if (isError) return <div>{t("common.errorFetchingData")}</div>;

    return (
        <Spin spinning={mutateLoading || isLoading}>
            {(parameterId && isFetched) || !parameterId ? (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <hr />
                    <Form.Item label={t("common.description")}>
                        <Text style={{ backgroundColor: '#428bca', color: 'white', padding: '3px' }}>{data?.body?.data.hotelParameter?.parameterDesc}</Text>
                    </Form.Item>

                    {renderInputField(data?.body?.data.hotelParameter?.parameterDataType!)}
                    <Form.Item label={data?.body?.data.hotelParameter?.parameterDescForUser}>
                    </Form.Item>
                    <Form.Item>
                        <Space align="center" style={{ justifyContent: "flex-end", display: "flex" }}>
                            <Button type="primary" htmlType="submit" loading={mutateLoading}>
                                {t("common.save")}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            ) : null}
        </Spin>
    );
});
